/**
 * @file: linking.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { withAiHistory } from '@euler/app/navigation/aiHistory';
import { rewriteAuthedPath } from '@euler/app/navigation/rewriteAuthedPath';
import { LinkingConfiguration } from '@euler/app/navigation/types';
import { linkingFactory } from '@euler/lib/linking';

// the linking configuration defines how to map state to paths and vice versa.
export const linking: LinkingConfiguration = {
  // eslint-disable-next-line @typescript-eslint/ban-types
  normal: linkingFactory<{}>(
    {
      screens: {
        // Main hosts the persistent drawer/sidebar (`Tabs` drawer screen),
        // which in turn hosts the tab navigator. All tab routes (Dashboard,
        // History, Plans, Me, Inbox) live under Main > Tabs.
        Main: {
          screens: {
            Tabs: {
              screens: {
                AiHome: { path: '/ai', exact: true },
                // The AI edition's tabs. Each nested page has a URL, so the
                // address the web shows after the BI entry replaces itself
                // reloads to the same page.
                AiMarketing: {
                  path: 'ai/marketing',
                  initialRouteName: 'ApdmDashboard',
                  screens: {},
                },
                AiDashboards: {
                  path: 'ai/dashboards',
                  initialRouteName: 'BiHome',
                  screens: {
                    BiDashboards: { path: 'ai/dashboards/all', exact: true },
                    BiExports: { path: 'ai/dashboards/exports', exact: true },
                    BiDashboard: {
                      path: 'ai/dashboards/:dashboardId',
                      exact: true,
                    },
                  },
                },
                AiCoaching: { path: 'ai/coaching', exact: true },
                Dashboard: {
                  path: '',
                  exact: true,
                },
                History: {
                  path: '/history',
                  exact: true,
                },
                Plans: {
                  path: '/plans',
                  exact: true,
                },
                // `path: 'leads'` maps `/leads` to the Leads stack's initial
                // route (the list); `/leads/:id` opens the management page and
                // `/leads/:leadId/new-activity` the log-follow-up form. The
                // book sub-screen stays link-less (reached from detail).
                // `initialRouteName` seeds the list beneath a deep-linked detail
                // so react-navigation renders a back button to it.
                Leads: {
                  path: 'leads',
                  initialRouteName: '_main',
                  screens: {
                    detail: {
                      path: 'leads/:id',
                      exact: true,
                    },
                    logActivity: {
                      path: 'leads/:leadId/new-activity',
                      exact: true,
                    },
                  },
                },
                // `initialRouteName` seeds the list (`_main`) beneath a
                // deep-linked detail so react-navigation renders a back button
                // to it (matching the Leads stack).
                Reservations: {
                  path: 'reservations',
                  initialRouteName: '_main',
                  screens: {
                    detail: {
                      path: 'reservations/:id',
                      exact: true,
                    },
                  },
                },
                // Settings and help are pages of the Me stack but keep their
                // own top-level URLs. `initialRouteName` seeds the Me home
                // beneath them so a cold deep link still has somewhere to go
                // back to — same pattern as the Leads/Reservations stacks.
                Me: {
                  path: 'me',
                  initialRouteName: '_home',
                  screens: {
                    settings: {
                      path: 'settings',
                      exact: true,
                    },
                    erpIntegration: {
                      path: 'settings/erp-integration',
                      exact: true,
                    },
                    help: {
                      path: 'help',
                      exact: true,
                    },
                  },
                },
                // `path: 'inbox'` maps `/inbox` to the inbox stack's
                // `initialRouteName` (`_inbox`). Don't give any sub-screen
                // an empty path — every nested `''` is treated as a
                // root-level pattern by react-navigation and collides with
                // Dashboard (also empty).
                Inbox: {
                  path: 'inbox',
                  screens: {
                    _feedThread: {
                      path: 'feed/threads/:threadId',
                      exact: true,
                    },
                    _settings: {
                      path: 'preferences',
                      exact: true,
                    },
                  },
                },
              },
            },
          },
        } as any,
        AiChat: {
          path: 'ai/chat/:sessionId?',
          exact: true,
        },
        PosterStudio: {
          path: 'ai/posters',
          exact: true,
        },
        Coaching: {
          path: 'coaching',
          initialRouteName: '_main',
          screens: {
            catalog: { path: 'coaching/cases', exact: true },
            case: { path: 'coaching/cases/:caseId', exact: true },
            related: { path: 'coaching/cases/:caseId/related', exact: true },
            collections: { path: 'coaching/collections', exact: true },
            collection: { path: 'coaching/collections/:slug', exact: true },
            history: { path: 'coaching/history', exact: true },
            practice: {
              path: 'coaching/practice/:attemptId',
              exact: true,
              parse: { momentTurn: Number },
            },
            report: {
              path: 'coaching/practice/:attemptId/report',
              exact: true,
            },
            transcript: {
              path: 'coaching/practice/:attemptId/transcript',
              exact: true,
            },
            leaderboard: { path: 'coaching/leaderboard', exact: true },
          },
        },
        ApdmCampaignWizard: {
          path: 'apdm/campaigns/wizard/:campaignId?',
          exact: true,
        },
        ApdmDashboard: {
          path: 'apdm',
          exact: true,
        },
        ApdmCampaign: {
          path: 'apdm/campaigns/:campaignId',
          exact: true,
        },
        ApdmImport: {
          path: 'apdm/imports/:importId?',
          exact: true,
        },
        BiHome: {
          path: 'bi',
          exact: true,
        },
        BiDashboards: {
          path: 'bi/dashboards',
          exact: true,
        },
        BiDashboard: {
          path: 'bi/dashboards/:dashboardId',
          exact: true,
        },
        BiDashboardCard: {
          path: 'bi/dashboards/:dashboardId/cards/:cardId',
          exact: true,
        },
        BiExports: {
          path: 'bi/exports',
          exact: true,
        },
        BiDatasets: {
          path: 'bi/datasets',
          exact: true,
        },
        BiDataset: {
          path: 'bi/datasets/:datasetRef',
          exact: true,
        },
        WorkshopBoard: {
          path: 'workshop-board',
          exact: true,
        },
        Task: {
          path: 'task/:taskNo',
          screens: {
            taskDetail: {
              path: 'task/:taskNo/detail',
              exact: true,
            },
          },
        },
        ServiceRecord: {
          path: 'service-record/:id',
          screens: {
            detail: {
              path: 'service-record/:id/detail',
              exact: true,
            },
            mediaAlbums: {
              path: 'service-record/:id/photos',
              exact: true,
            },
          },
        },
        // Dev builds only, like the screen itself (`DevScreens`): in a
        // release build `/playground` falls through to NotFound.
        ...(__DEV__
          ? {
              Playground: {
                path: 'playground',
                initialRouteName: 'Main',
                screens: {
                  BiCharts: {
                    path: 'playground/bi-charts',
                    exact: true,
                  },
                  BiChartCategory: {
                    path: 'playground/bi-charts/:category',
                    exact: true,
                  },
                  BiDashboards: {
                    path: 'playground/bi-dashboards',
                    exact: true,
                  },
                  BiVisualization: {
                    path: 'playground/bi-visualization',
                    exact: true,
                  },
                  BiUploads: {
                    path: 'playground/bi-uploads',
                    exact: true,
                  },
                },
              },
            }
          : {}),
        NotFound: '*',
      },
      // ensure home will always be the first route
    },
    {
      rewritePath: rewriteAuthedPath,
      rewriteState: withAiHistory,
      openLinksOnTop: true,
    },
  ),
  // eslint-disable-next-line @typescript-eslint/ban-types
  auth: linkingFactory<{}>({
    screens: {
      Auth: {
        path: '',
        screens: {
          Login: 'login',
        },
      },
    },
  }),
};
